// src/hooks/useAnnouncementsFirestore.js
import { useState, useEffect } from 'react';
import { collection, query, orderBy, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';

/**
 * 공지사항 Firestore Hook (onSnapshot 실시간 버전)
 * 
 * 비용 최적화:
 * - onSnapshot으로 실시간 업데이트 (캐싱 불필요)
 * - 공지는 자주 변경되지 않아 읽기 비용 최소 (월 5회 예상)
 * - 읽음 상태는 localStorage
 * 
 * 비용: 초기 3 reads + 공지 추가/수정 시 1 read = 월 5 reads (기존 30 reads 대비 83% 절감)
 */

const READ_IDS_KEY = 'announcement_read_ids';

// 환경별 컬렉션명
const getAnnouncementCollection = (environment) => {
  if (!environment || environment === 'production' || environment === 'mainnet') {
    return 'announcements';
  }
  if (environment === 'testnet' || environment === 'sandbox') {
    return 'testnet_announcements';
  }
  return 'dev_announcements';
};

// 🎯 [Cost Optimization] 싱글톤 리스너 시스템 (전역 캐싱)
let globalAnnouncements = {}; // 환경별 캐시
let globalUnsubscribes = {};
let globalListeners = {}; // 환경별 리스너 콜백

export const useAnnouncementsFirestore = (environment = 'testnet') => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const [announcements, setAnnouncements] = useState(globalAnnouncements[environment] || []);
  const [loading, setLoading] = useState(!globalAnnouncements[environment]);
  const [readIds, setReadIds] = useState([]);
  const [hasUnread, setHasUnread] = useState(false);

  // localStorage에서 읽은 공지 ID 로드
  useEffect(() => {
    try {
      const stored = localStorage.getItem(READ_IDS_KEY);
      if (stored) {
        setReadIds(JSON.parse(stored));
      }
    } catch (error) {
      console.error('[useAnnouncements] Error loading read IDs:', error);
    }
  }, []);

  // 🔥 onSnapshot으로 실시간 리스닝 (싱글톤)
  useEffect(() => {
    // 해당 인스턴스의 state 갱신용
    const listener = (data) => {
      setAnnouncements(data);
      setLoading(false);
    };

    if (!globalListeners[environment]) {
      globalListeners[environment] = [];
    }
    globalListeners[environment].push(listener);

    if (!globalUnsubscribes[environment]) {
      console.log(`[useAnnouncements] Starting GLOBAL real-time listener for ${environment}`);
      const collectionName = getAnnouncementCollection(environment);
      const q = query(
        collection(db, collectionName),
        where('active', '==', true),
        orderBy('priority', 'desc'),
        orderBy('createdAt', 'desc')
      );

      // 실시간 리스너 등록
      globalUnsubscribes[environment] = onSnapshot(
        q,
        (snapshot) => {
          const items = [];
          snapshot.forEach((doc) => {
            items.push({
              id: doc.id,
              ...doc.data()
            });
          });

          console.log('[useAnnouncements] Real-time update:', items.length, 'announcements');
          globalAnnouncements[environment] = items;

          // 구독중인 모든 컴포넌트에 새 데이터 브로드캐스트
          globalListeners[environment].forEach(l => l(items));
        },
        (error) => {
          console.error('[useAnnouncements] onSnapshot error:', error);
          globalListeners[environment].forEach(l => l([]));
        }
      );
    } else {
      // 이미 구독 중이고 데이터가 있다면 즉시 반환
      if (globalAnnouncements[environment]) {
        listener(globalAnnouncements[environment]);
      }
    }

    // cleanup: 컴포넌트 언마운트 시 리스너 해제
    return () => {
      globalListeners[environment] = globalListeners[environment].filter(l => l !== listener);

      // 앱 내에서 아무도 구독하지 않으면 커넥션 완전 차단 (비용 세이브)
      if (globalListeners[environment].length === 0 && globalUnsubscribes[environment]) {
        console.log(`[useAnnouncements] Unsubscribing GLOBAL listener for ${environment}`);
        globalUnsubscribes[environment]();
        globalUnsubscribes[environment] = null;
        globalAnnouncements[environment] = null;
      }
    };
  }, [environment]);

  // 읽지 않은 공지 체크
  useEffect(() => {
    if (announcements.length === 0) {
      setHasUnread(false);
      return;
    }

    const unread = announcements.some(item => !readIds.includes(item.id));
    setHasUnread(unread);
  }, [announcements, readIds]);

  // 공지 읽음 처리
  const markAsRead = (announcementId) => {
    if (readIds.includes(announcementId)) return;

    const newReadIds = [...readIds, announcementId];
    setReadIds(newReadIds);

    try {
      localStorage.setItem(READ_IDS_KEY, JSON.stringify(newReadIds));
    } catch (error) {
      console.error('[useAnnouncements] Error saving read IDs:', error);
    }
  };

  // 모든 공지 읽음 처리
  const markAllAsRead = () => {
    const allIds = announcements.map(item => item.id);
    setReadIds(allIds);

    try {
      localStorage.setItem(READ_IDS_KEY, JSON.stringify(allIds));
    } catch (error) {
      console.error('[useAnnouncements] Error saving read IDs:', error);
    }
  };

  // 언어에 맞는 번역 가져오기
  const getTranslation = (item) => {
    if (!item || !item.translations) return null;

    // 1. 정확한 매칭
    let translation = item.translations[currentLanguage];

    // 2. [추가] 앞부분만 매칭 (예: zh-CN -> zh, en-US -> en)
    if (!translation && currentLanguage.includes('-')) {
      const shortLang = currentLanguage.split('-')[0];
      translation = item.translations[shortLang];
    }

    // 3. Fallback to English
    translation = translation || item.translations['en'];

    // 4. [추가] Fallback to Korean (Source) if English is missing
    translation = translation || item.translations['ko'];
    if (!translation) return null;

    return {
      ...item,
      title: translation.title,
      content: translation.content,
      formattedContent: translation.content.replace(/\\n/g, '\n').split('\n'),
      isRead: readIds.includes(item.id)
    };
  };

  // 모든 공지사항 (번역 적용)
  const allAnnouncements = announcements.map(getTranslation).filter(Boolean);

  // 최신 공지사항
  const latestAnnouncement = allAnnouncements.length > 0 ? allAnnouncements[0] : null;

  // 읽지 않은 공지사항 개수
  const unreadCount = allAnnouncements.filter(item => !item.isRead).length;

  return {
    allAnnouncements,
    latestAnnouncement,
    hasAnnouncement: allAnnouncements.length > 0,
    hasUnread,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    // onSnapshot은 자동 업데이트되므로 refresh 불필요 (호환성 유지용)
    refresh: () => console.log('[useAnnouncements] Auto-refreshing via onSnapshot')
  };
};

export default useAnnouncementsFirestore;
